/** @jsxImportSource @emotion/react */

import { useMutation } from '@apollo/client'
import Button from '@mui/material/Button'
import MenuItem from '@mui/material/MenuItem'
import Typography from '@mui/material/Typography'
import { FC } from 'react'
import { Form } from 'react-final-form'
import { MenuSelectField } from 'components/Select/MenuSelectField'
import { GroupIconsAWS } from 'constants/consts'

import { createFlashCardGroup, createFlashCardGroupVariables } from '__generatedTypes__/createFlashCardGroup'
import { findAllFlashCardGroup_findAllFlashCardGroup_items as IGroup } from '__generatedTypes__/findAllFlashCardGroup'
import { GroupIcon } from '__generatedTypes__/globalTypes'
import { removeFlashCardGroup, removeFlashCardGroupVariables } from '__generatedTypes__/removeFlashCardGroup'
import { updateFlashCardGroup, updateFlashCardGroupVariables } from '__generatedTypes__/updateFlashCardGroup'
import { ReactComponent as TrashIcon } from 'assets/icons/trash.svg'
import TextareaAutosize from 'components/TextareaAutosize/TextareaAutosize'
import client from 'configs/apolloClient'
import {
  CREATE_FLASH_CARD_GROUP,
  FIND_ALL_FLASH_CARD_GROUP,
  REMOVE_FLASH_CARD_GROUP,
  UPDATE_FLASH_CARD_GROUP,
} from 'queries/flashCardsGroup'
import { InputField } from 'components/Input'
import { validateRequired } from 'utils/validation'
import { FieldValues } from 'utils/types'
import { toast } from 'utils/toast'
import * as commonClasses from 'components/shared/GroupCards/commonStyles'
import * as classes from './styles'

interface IProps {
  group: IGroup | null
  availableGroupColors: string[]
  handleClose: () => void
  newGroupOrder: number
  selectedSubjectId: string
}

const ManageFlashCardsGroupModal: FC<IProps> = ({
  group,
  newGroupOrder,
  selectedSubjectId,
  availableGroupColors,
  handleClose,
}) => {
  const colors = group ? [...availableGroupColors, group.color] : availableGroupColors
  const [updateFlashCardGroup, { loading: updateLoading }] = useMutation<
    updateFlashCardGroup,
    updateFlashCardGroupVariables
  >(UPDATE_FLASH_CARD_GROUP)

  const [removeFlashCardGroup] = useMutation<removeFlashCardGroup, removeFlashCardGroupVariables>(
    REMOVE_FLASH_CARD_GROUP
  )

  const handleCloseAndUpdate = () => {
    handleClose()
    client.refetchQueries({
      include: [FIND_ALL_FLASH_CARD_GROUP],
    })
  }

  const [createFlashCardGroup, { loading: createLoading }] = useMutation<
    createFlashCardGroup,
    createFlashCardGroupVariables
  >(CREATE_FLASH_CARD_GROUP)

  const handleGroupRemove = async () => {
    if (!group?.id) return
    try {
      //TODO Error handling
      await removeFlashCardGroup({ variables: { removeFlashCardGroupId: group.id } })
      handleCloseAndUpdate()

      toast.success('Group deleted successfully')
    } catch (error) {
      toast.error('Failed to delete group')
    }
  }

  // TODO: specify proper type
  const handleSubmit = async (data: FieldValues) => {
    if (group) {
      try {
        await updateFlashCardGroup({
          variables: {
            updateFlashCardGroupInputFieldName: {
              id: data.id,
              color: data.color,
              title: data.title,
              icon: data.icon,
              description: data.description ?? '',
            },
          },
        })
        handleCloseAndUpdate()

        toast.success('Group updated successfully')
      } catch (error) {
        toast.error('Failed to update group')
      }
    } else {
      try {
        await createFlashCardGroup({
          variables: {
            createFlashCardGroupInput: {
              color: data.color,
              title: data.title,
              description: data.description ?? '',
              icon: data.icon,
              order: newGroupOrder,
              subject: {
                id: selectedSubjectId,
              },
            },
          },
        })
        handleCloseAndUpdate()

        toast.success('Group created successfully')
      } catch (error) {
        toast.error('Failed to create group')
      }
    }
  }

  return (
    <div css={commonClasses.modalContainer}>
      <Form
        onSubmit={handleSubmit}
        initialValues={group}
        render={({ handleSubmit, values }) => (
          <form css={commonClasses.modalForm} onSubmit={handleSubmit}>
            <Typography css={commonClasses.modalTitle} variant="title2">
              {group ? 'Edit Group' : 'Create Group'}
            </Typography>
            <InputField name="title" validate={validateRequired} label="Group Title" fullWidth />
            <div css={classes.titleColor}>
              <Typography variant="body2" css={commonClasses.label}>
                Color
              </Typography>
              <Typography variant="body2" css={commonClasses.label}>
                Category
              </Typography>
              <MenuSelectField css={commonClasses.select} fullWidth name="color" required>
                {colors.map((color) => (
                  <MenuItem value={color} key={color} css={classes.menuItem}>
                    <div css={classes.menuItemColor(color)} />
                  </MenuItem>
                ))}
              </MenuSelectField>
              <MenuSelectField css={commonClasses.select} fullWidth name="icon" required>
                {Object.values(GroupIcon).map((icon: GroupIcon) => (
                  <MenuItem value={icon} key={icon} css={classes.menuItem}>
                    <img src={GroupIconsAWS[icon]} alt="Group Icons" css={classes.icon} />
                  </MenuItem>
                ))}
              </MenuSelectField>
            </div>
            <Typography variant="body2" css={commonClasses.label}>
              Description
            </Typography>
            <TextareaAutosize value={values.description} css={commonClasses.descriptionInput} name="description" />
            <div css={commonClasses.buttons}>
              {!!group && (
                <Button
                  css={commonClasses.button}
                  variant="outlined"
                  color="red"
                  size="large"
                  onClick={handleGroupRemove}
                >
                  <TrashIcon />
                  Delete
                </Button>
              )}
              <Button css={commonClasses.button} variant="outlined" color="blue" size="large" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                css={[commonClasses.button, !!group && commonClasses.fullButton]}
                variant="contained"
                type="submit"
                color="blue"
                size="large"
              >
                {/*TODO Change loading after Nazrin`s answer*/}
                {createLoading || updateLoading ? '...Loading' : 'Submit'}
              </Button>
            </div>
          </form>
        )}
      />
    </div>
  )
}

export default ManageFlashCardsGroupModal
